import { useEffect, useMemo, useState } from 'react';

import {
  Accordion,
  ChakraBox,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  DateFormat,
  DatePickerInput,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ToastMessage,
  getOutOfOfficeDateStatus,
  useToastMessage,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import {
  ProfileEventsAdmin,
  ProfileEventsMaud,
} from '~/analytics/profileEvents';
import { ACCORDION_WIDTH_LARGE, DATE_PLACEHOLDER } from '~/lib/constants';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { UserMode, useModeContext } from '~/lib/contexts/ModeContext';
import {
  useAddOutOfOfficeMutation,
  useDeleteOutOfOfficeMutation,
  useEditOutOfOfficeMutation,
} from '~/store/services/profile';

import DeleteConfirmModal from './DeleteConfirmModal';
import DiscardChangesModalV2 from '../DiscardChangesModalV2';

// OOO indicates Out of Office
const TOP_LEVEL_ID = 'out-of-office-accordion';

type OOOErrors = {
  startDate: boolean;
  endDate: boolean;
  pastDates: boolean;
};
interface OutOfOfficeAccordionProps {
  oooData?: OOOAccordionData;
  onDismissOOOAccordion?: () => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
}

const OutOfOfficeAccordion = ({
  oooData,
  onDismissOOOAccordion,
  isExpanded,
  setIsExpanded,
}: OutOfOfficeAccordionProps) => {
  const { currentUser } = useGlobalContext();

  const [addOutOfOffice, addOutOfOfficeResult] = useAddOutOfOfficeMutation();
  const [editOutOfOffice, editOutOfOfficeResult] = useEditOutOfOfficeMutation();
  const [deleteOutOfOffice, deleteOutOfOfficeResult] =
    useDeleteOutOfOfficeMutation();
  const [isStartDateValid, setIsStartDateValid] = useState<boolean>(true);
  const [isEndDateValid, setIsEndDateValid] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState<boolean>(false);
  const [isUnsavedOOOChanges, setIsUnsavedOOOChanges] =
    useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState<boolean>(false);
  const { createToast } = useToastMessage();
  const oooAccordionType = oooData?.type ?? 'Add';
  const isSubmitOOOError =
    addOutOfOfficeResult.isError ||
    editOutOfOfficeResult.isError ||
    deleteOutOfOfficeResult.isError;
  const isSubmitOOOSuccess =
    addOutOfOfficeResult.isSuccess || editOutOfOfficeResult.isSuccess;
  const isLoading =
    addOutOfOfficeResult.isLoading || editOutOfOfficeResult.isLoading;
  const oooStatus: OutOfOfficeStatus = getOutOfOfficeDateStatus(
    oooData?.outOfOfficeData?.startDate,
    oooData?.outOfOfficeData?.endDate,
  );
  const { trackEvent } = useAnalytics();
  const { mode } = useModeContext();
  const isAdmin = mode === UserMode.ADMIN;

  useEffect(() => {
    addOutOfOfficeResult.reset();
    editOutOfOfficeResult.reset();
    setIsEditing(false);
    if (oooData?.outOfOfficeData) {
      try {
        const { startDate, endDate } = oooData.outOfOfficeData;
        if (startDate) setStartDate(DateTime.fromISO(startDate).toJSDate());
        if (endDate) setEndDate(DateTime.fromISO(endDate).toJSDate());
      } catch (error) {
        console.error(error);
      }
    }
  }, [oooData]);

  const onResetAndClose = (): void => {
    setIsUnsavedOOOChanges(false);
    setIsUnsavedChangesModalOpen(false);
    setStartDate(new Date());
    setEndDate(new Date());
    onDismissOOOAccordion();
    setIsEditing(false);
  };

  const handleCancelAndClose = (): void => {
    if (isUnsavedOOOChanges || isSubmitOOOError) {
      setIsUnsavedChangesModalOpen(true);
    } else onResetAndClose();
  };

  const handleDeleteConfirm = () => {
    trackEvent(
      isAdmin
        ? ProfileEventsAdmin.adminOutOfOfficeDeleteConfirmationModalDeleteOutOfOfficeButtonClicked
        : ProfileEventsMaud.maudOutOfOfficeDeleteConfirmationModalDeleteOutOfOfficeButtonClicked,
      {
        source: isAdmin
          ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
          : AnalyticsSourceValues.PROFILE_TAB_MAUD,
      },
    );
    const outOfOfficeId = oooData?.outOfOfficeData?.outOfOfficeId;
    const providerExternalId = oooData?.providerExternalId;
    deleteOutOfOffice({
      outOfOfficeId,
      providerExternalId: providerExternalId ?? currentUser.userId,
    })
      .unwrap()
      .then(() => {
        onResetAndClose();
      })
      .catch(() => {
        setIsDeleteConfirmationModalOpen(false);
      });
  };

  const checkRequiredFields = (): boolean => {
    if ((startDate && !isStartDateValid) || !startDate) return true;
    if ((endDate && !isEndDateValid) || !endDate) return true;

    return false;
  };

  const OOOErrors = useMemo<OOOErrors>(
    () => ({
      endDate: !isEndDateValid,
      startDate: !isStartDateValid,
      ...(oooAccordionType === 'Add' && {
        pastDates:
          // this can likely be simplified, but seems like tests are missing for it
          DateTime.fromISO(
            DateTime.fromJSDate(startDate).toFormat(
              DateFormat.shortYearMonthDay,
            ),
          ).startOf('day') < DateTime.now().startOf('day') ||
          DateTime.fromISO(
            DateTime.fromJSDate(endDate).toFormat(DateFormat.shortYearMonthDay),
          ).startOf('day') < DateTime.now().startOf('day'),
      }),
    }),
    [isStartDateValid, isEndDateValid, oooAccordionType, startDate, endDate],
  );

  const handleConfirmClick = (): void => {
    if (oooAccordionType === 'View')
      trackEvent(
        isAdmin
          ? ProfileEventsAdmin.adminEditOutOfOfficeSaveButtonClicked
          : ProfileEventsMaud.maudEditOutOfOfficeSaveButtonClicked,
        {
          source: isAdmin
            ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
            : AnalyticsSourceValues.PROFILE_TAB_MAUD,
        },
      );
    else
      trackEvent(
        isAdmin
          ? ProfileEventsAdmin.adminAddOutOfOfficeSaveButtonClicked
          : ProfileEventsMaud.maudAddOutOfOfficeSaveButtonClicked,
        {
          source: isAdmin
            ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
            : AnalyticsSourceValues.PROFILE_TAB_MAUD,
        },
      );
    const hasErrors = checkRequiredFields();
    if (hasErrors) return;
    const outOfOfficeId = oooData?.outOfOfficeData?.outOfOfficeId;
    const providerExternalId = oooData?.providerExternalId;

    if (outOfOfficeId) {
      editOutOfOffice({
        endDate: DateTime.fromJSDate(endDate).toFormat(
          DateFormat.shortYearMonthDay,
        ),
        outOfOfficeId,
        providerExternalId: providerExternalId ?? currentUser.userId,
        startDate: DateTime.fromJSDate(startDate).toFormat(
          DateFormat.shortYearMonthDay,
        ),
      })
        .unwrap()
        .then(() => {
          createToast({
            icon: FAIconName.checkRegular,
            id: 'edit-ooo-toast',
            text: 'Out of office updated',
            variant: 'success',
          });
          onResetAndClose();
        });
    } else {
      addOutOfOffice({
        endDate: DateTime.fromJSDate(endDate).toFormat(
          DateFormat.shortYearMonthDay,
        ),
        providerExternalId: providerExternalId ?? currentUser.userId,
        startDate: DateTime.fromJSDate(startDate).toFormat(
          DateFormat.shortYearMonthDay,
        ),
      })
        .unwrap()
        .then(() => {
          createToast({
            icon: FAIconName.checkRegular,
            id: 'add-ooo-toast',
            text: 'Out of office added',
            variant: 'success',
          });
          onResetAndClose();
        });
    }
  };

  const handleDelete = () => {
    trackEvent(
      isAdmin
        ? ProfileEventsAdmin.adminOutOfOfficeDeleteButtonClicked
        : ProfileEventsMaud.maudOutOfOfficeDeleteButtonClicked,
      {
        source: isAdmin
          ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
          : AnalyticsSourceValues.PROFILE_TAB_MAUD,
      },
    );
    setIsDeleteConfirmationModalOpen(true);
  };

  const handleEditClick = (): void => {
    trackEvent(
      isAdmin
        ? ProfileEventsAdmin.adminOutOfOfficeEditButtonClicked
        : ProfileEventsMaud.maudOutOfOfficeEditButtonClicked,
      {
        source: isAdmin
          ? AnalyticsSourceValues.PROFILE_TAB_ADMIN
          : AnalyticsSourceValues.PROFILE_TAB_MAUD,
      },
    );
    setIsEditing(true);
  };

  const handleDateChange = (key: 'startDate' | 'endDate', date: Date) => {
    setIsUnsavedOOOChanges(true);

    if (key === 'startDate') {
      setStartDate(date);
      setIsStartDateValid(
        DateTime.fromJSDate(date).startOf('day') >=
          DateTime.now().startOf('day'),
      );
      if (endDate < date) {
        setEndDate(date);
        setIsEndDateValid(
          DateTime.fromJSDate(date).startOf('day') >=
            DateTime.now().startOf('day'),
        );
      }
    } else if (key === 'endDate') {
      setEndDate(date);
      setIsEndDateValid(
        DateTime.fromJSDate(date).startOf('day') >=
          DateTime.fromJSDate(startDate).startOf('day'),
      );
    }
  };

  const footerContent =
    oooAccordionType === 'View' && !isEditing ? (
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}__delete`}
          leftIcon={FAIconName.trashRegular}
          onClick={handleDelete}
          variant="secondary"
        >
          Delete
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__confirm`}
          leftIcon={FAIconName.penRegular}
          onClick={handleEditClick}
        >
          Edit
        </FireflyButton>
      </ChakraFlex>
    ) : (
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}__cancel`}
          leftIcon={FAIconName.XmarkRegular}
          onClick={handleCancelAndClose}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__confirm`}
          isDisabled={Object.values(OOOErrors).some((val: boolean) => val)}
          isLoading={isLoading}
          leftIcon={FAIconName.checkRegular}
          onClick={handleConfirmClick}
        >
          Save changes
        </FireflyButton>
      </ChakraFlex>
    );

  return (
    <>
      <DiscardChangesModalV2
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onConfirm={onResetAndClose}
      />
      <Accordion
        dismissMenu={handleCancelAndClose}
        footer={!isSubmitOOOSuccess && footerContent}
        id={TOP_LEVEL_ID}
        isExpanded={isExpanded}
        panelMaxWidth={!isSubmitOOOSuccess && ACCORDION_WIDTH_LARGE}
        panelMinWidth={!isSubmitOOOSuccess && ACCORDION_WIDTH_LARGE}
        setIsExpanded={setIsExpanded}
        title={(isEditing ? 'Edit' : oooAccordionType) + ' out of office'}
      >
        <ChakraBox gap="10px" padding="20px">
          <ChakraSimpleGrid columns={2} spacing="16px">
            <ChakraGridItem gap="6px">
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__start-date`}
                isRequired
                label="Start date"
              >
                <DatePickerInput
                  defaultDate={startDate}
                  id={`${TOP_LEVEL_ID}__start-date`}
                  isDisabled={isEditing && oooStatus === 'active'}
                  isReadOnly={oooAccordionType === 'View' && !isEditing}
                  minDate={new Date()}
                  onSubmit={(date: Date) => handleDateChange('startDate', date)}
                  placeholder={DATE_PLACEHOLDER}
                />
              </InputGroupV2>
            </ChakraGridItem>
            <ChakraGridItem>
              <InputGroupV2
                id={`${TOP_LEVEL_ID}__end-date`}
                isRequired
                label="End date"
              >
                <DatePickerInput
                  defaultDate={endDate}
                  id={`${TOP_LEVEL_ID}__end-date`}
                  isReadOnly={oooAccordionType === 'View' && !isEditing}
                  minDate={
                    isEditing && oooStatus === 'active' ? new Date() : startDate
                  }
                  onSubmit={(date: Date) => handleDateChange('endDate', date)}
                  placeholder={DATE_PLACEHOLDER}
                />
              </InputGroupV2>
            </ChakraGridItem>
          </ChakraSimpleGrid>
          {isSubmitOOOError && (
            <ToastMessage
              icon={FAIconName.exclamationTriangleRegular}
              id={`${TOP_LEVEL_ID}__warning`}
              isFullWidth
              text="We’re sorry, we were unable to save your changes. Please try
                  again."
              variant="critical"
            />
          )}
        </ChakraBox>
      </Accordion>
      <DeleteConfirmModal
        buttonTitle="Delete out of office"
        isOpen={isDeleteConfirmationModalOpen}
        isPrimaryButtonDisabled={deleteOutOfOfficeResult?.isLoading}
        onConfirmClick={handleDeleteConfirm}
        setIsOpen={setIsDeleteConfirmationModalOpen}
        subTitle="Are you sure you want to delete this out of office? Once deleted, it can’t be recovered."
        title="Delete out of office"
      />
    </>
  );
};

export default OutOfOfficeAccordion;
